import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  PlusIcon,
  PencilSquareIcon,
  ArchiveBoxIcon,
  TrashIcon,
  PhotoIcon,
  XMarkIcon,
  ListBulletIcon,
  AcademicCapIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import PageHeader from "../../components/layout/PageHeader";
import { Table } from "../../components/ui/Table";
import { Badge } from "../../components/ui/Badge";
import { Modal } from "../../components/ui/Modal";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { activitiesApi } from "../../api/activities";
import { KERCHIEF_SLOT_COUNT, layoutKerchief } from "../../utils/kerchiefSlots";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import type { Activity, AchievementCredit } from "../../types";

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  location: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

const creditSchema = z.object({
  code: z.string().min(1, "Code is required"),
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
});

type CreditFormData = z.infer<typeof creditSchema>;


export default function ManageActivities() {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingActivity, setEditingActivity] = useState<Activity | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState<{ id: number; name: string } | null>(null);
  const [badgeFile, setBadgeFile] = useState<File | null>(null);
  const [badgePreview, setBadgePreview] = useState<string | null>(null);
  const [badgeRemoved, setBadgeRemoved] = useState(false);
  const badgeInputRef = useRef<HTMLInputElement>(null);
  const deepLinkHandled = useRef(false);
  const creditFormRef = useRef<HTMLFormElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Credits management state
  const [creditsModalOpen, setCreditsModalOpen] = useState(false);
  const [managingActivity, setManagingActivity] = useState<Activity | null>(null);
  const [credits, setCredits] = useState<AchievementCredit[]>([]);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [showArchivedCredits, setShowArchivedCredits] = useState(false);
  const [creditFormOpen, setCreditFormOpen] = useState(false);
  const [editingCredit, setEditingCredit] = useState<AchievementCredit | null>(null);
  const [savingCredit, setSavingCredit] = useState(false);
  const [archivingCredit, setArchivingCredit] = useState(false);
  const [deleteCreditConfirm, setDeleteCreditConfirm] = useState(false);
  const [creditToDelete, setCreditToDelete] = useState<AchievementCredit | null>(null);
  const [deletingCredit, setDeletingCredit] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [reorderingActivity, setReorderingActivity] = useState(false);

  // Always fetch archived too: archived activities keep a kerchief sort
  // position, so reorder calls must send the complete ordering. The table
  // filters them out client-side when the toggle is off.
  const fetchActivities = useCallback(() => {
    setLoading(true);
    activitiesApi
      .list(true)
      .then(setActivities)
      .catch(() => toast.error("Failed to load activities"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const {
    register: registerCredit,
    handleSubmit: handleSubmitCredit,
    reset: resetCredit,
    formState: { errors: creditErrors },
  } = useForm<CreditFormData>({ resolver: zodResolver(creditSchema) });

  const openCreate = () => {
    setEditingActivity(null);
    reset({ name: "", description: "", location: "" });
    setBadgeFile(null);
    setBadgePreview(null);
    setBadgeRemoved(false);
    setModalOpen(true);
  };

  const openEdit = (activity: Activity) => {
    setEditingActivity(activity);
    reset({
      name: activity.name,
      description: activity.description ?? "",
      location: activity.location ?? "",
    });
    setBadgeFile(null);
    setBadgePreview(activity.thumbnailUrl ?? null);
    setBadgeRemoved(false);
    setModalOpen(true);
  };

  // Deep-link: open edit modal when ?edit=<id> is in the URL
  useEffect(() => {
    if (loading || deepLinkHandled.current) return;
    const editId = searchParams.get("edit");
    if (!editId) return;
    const activity = activities.find((a) => a.id === Number(editId));
    if (activity) {
      openEdit(activity);
      deepLinkHandled.current = true;
      setSearchParams((p) => { p.delete("edit"); return p; }, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, activities]);

  useEffect(() => {
    if (creditFormOpen) {
      creditFormRef.current?.scrollIntoView({ block: "start" });
    }
  }, [creditFormOpen, editingCredit]);

  const onSubmit = async (data: FormData) => {
    setSaving(true);
    try {
      let savedActivity: Activity;
      if (editingActivity) {
        savedActivity = await activitiesApi.update(editingActivity.id, data as Partial<Omit<Activity, "id" | "achievementCredits">>);
      } else {
        savedActivity = await activitiesApi.create(data as Omit<Activity, "id" | "achievementCredits">);
      }
      if (badgeFile) {
        await activitiesApi.uploadBadge(savedActivity.id, badgeFile);
      } else if (badgeRemoved && editingActivity) {
        await activitiesApi.removeBadge(editingActivity.id);
      }
      toast.success(editingActivity ? "Activity updated!" : "Activity created!");
      reset();
      setModalOpen(false);
      setEditingActivity(null);
      setBadgeFile(null);
      setBadgePreview(null);
      setBadgeRemoved(false);
      fetchActivities();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `Failed to ${editingActivity ? "update" : "create"} activity`);
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!editingActivity) return;
    setArchiving(true);
    try {
      await activitiesApi.archive(editingActivity.id);
      toast.success(editingActivity.archivedAt ? "Activity restored!" : "Activity archived!");
      setModalOpen(false);
      setEditingActivity(null);
      fetchActivities();
    } catch {
      toast.error("Failed to archive activity");
    } finally {
      setArchiving(false);
    }
  };

  const handleDelete = async () => {
    if (!activityToDelete) return;
    setDeleting(true);
    try {
      await activitiesApi.delete(activityToDelete.id);
      toast.success("Activity permanently deleted!");
      setDeleteConfirm(false);
      setActivityToDelete(null);
      fetchActivities();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || "Failed to delete activity");
    } finally {
      setDeleting(false);
    }
  };

  const fetchCredits = useCallback((activityId: number) => {
    setCreditsLoading(true);
    activitiesApi
      .getCredits(activityId)
      .then(setCredits)
      .catch(() => toast.error("Failed to load credits"))
      .finally(() => setCreditsLoading(false));
  }, []);

  const openManageCredits = (activity: Activity) => {
    setManagingActivity(activity);
    setShowArchivedCredits(false);
    setCreditFormOpen(false);
    setEditingCredit(null);
    setCredits([]);
    setCreditsModalOpen(true);
    fetchCredits(activity.id);
    setModalOpen(false);
    setEditingActivity(null);
  };

  const openCreditCreate = () => {
    setEditingCredit(null);
    resetCredit({ code: "", name: "", description: "" });
    setCreditFormOpen(true);
  };

  const openCreditEdit = (credit: AchievementCredit) => {
    setEditingCredit(credit);
    resetCredit({ code: credit.code, name: credit.name, description: credit.description ?? "" });
    setCreditFormOpen(true);
  };

  const onSubmitCredit = async (data: CreditFormData) => {
    if (!managingActivity) return;
    setSavingCredit(true);
    try {
      if (editingCredit) {
        await activitiesApi.updateCredit(managingActivity.id, editingCredit.id, data);
        toast.success("Credit updated!");
      } else {
        await activitiesApi.addCredit(managingActivity.id, data);
        toast.success("Credit added!");
      }
      setCreditFormOpen(false);
      setEditingCredit(null);
      fetchCredits(managingActivity.id);
      fetchActivities();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save credit");
    } finally {
      setSavingCredit(false);
    }
  };

  const handleArchiveCredit = async (credit: AchievementCredit) => {
    if (!managingActivity) return;
    setArchivingCredit(true);
    try {
      await activitiesApi.archiveCredit(managingActivity.id, credit.id);
      toast.success(credit.archivedAt ? "Credit restored!" : "Credit archived!");
      fetchCredits(managingActivity.id);
    } catch {
      toast.error("Failed to archive credit");
    } finally {
      setArchivingCredit(false);
    }
  };

  const handleDeleteCredit = async () => {
    if (!managingActivity || !creditToDelete) return;
    setDeletingCredit(true);
    try {
      await activitiesApi.deleteCredit(managingActivity.id, creditToDelete.id);
      toast.success("Credit permanently deleted!");
      setDeleteCreditConfirm(false);
      setCreditToDelete(null);
      fetchCredits(managingActivity.id);
      fetchActivities();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || "Failed to delete credit");
    } finally {
      setDeletingCredit(false);
    }
  };

  // Derived once per render — table data, arrow buttons and reorder handlers
  // all index into these instead of re-filtering per row.
  const activeActivities = useMemo(() => activities.filter((a) => !a.archivedAt), [activities]);
  const activeActivityIndex = useMemo(
    () => new Map(activeActivities.map((a, i) => [a.id, i])),
    [activeActivities]
  );
  // Active (non-archived, non-graduation) credits in their current display order
  const activeCredits = useMemo(
    () => credits.filter((c) => !c.archivedAt && c.type !== "graduation"),
    [credits]
  );
  const activeCreditIndex = useMemo(
    () => new Map(activeCredits.map((c, i) => [c.id, i])),
    [activeCredits]
  );

  const handleReorderCredit = async (index: number, direction: "up" | "down") => {
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= activeCredits.length) return;

    // Swap in the active list then rebuild the full credits list
    const swapped = [...activeCredits];
    [swapped[index], swapped[swapIndex]] = [swapped[swapIndex], swapped[index]];

    // Rebuild: replace active credits in-place, preserve archived & graduation order
    const activeSet = new Set(activeCredits.map((c) => c.id));
    let swappedIdx = 0;
    let displayIdx = 1;
    const next = credits.map((c) => {
      if (activeSet.has(c.id)) {
        const ac = swapped[swappedIdx++];
        return { ...ac, displayNumber: displayIdx++ };
      }
      return c;
    });
    setCredits(next);

    if (!managingActivity) return;
    setReordering(true);
    try {
      await activitiesApi.reorderCredits(
        managingActivity.id,
        swapped.map((c) => c.id)
      );
    } catch {
      fetchCredits(managingActivity.id);
      toast.error("Failed to reorder credits");
    } finally {
      setReordering(false);
    }
  };

  // Printed activities and write-ins only reorder among themselves — moving
  // between the two is done in the Kerchief Layout editor.
  const sameKerchiefGroup = (a: Activity, b: Activity) =>
    (a.onKerchief === false) === (b.onKerchief === false);

  const handleReorderActivity = async (index: number, direction: "up" | "down") => {
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= activeActivities.length) return;
    const a = activeActivities[index];
    const b = activeActivities[swapIndex];
    if (!sameKerchiefGroup(a, b)) return;

    // Swap the two activities' kerchief positions (a printed sort_order is
    // its physical slot); every other activity, archived included, stays put.
    const { slots, writeIns } = layoutKerchief(activities);
    const swapIn = (list: (Activity | null)[]) => {
      const i = list.findIndex((x) => x?.id === a.id);
      const j = list.findIndex((x) => x?.id === b.id);
      if (i < 0 || j < 0) return false;
      [list[i], list[j]] = [list[j], list[i]];
      return true;
    };
    if (!swapIn(slots) && !swapIn(writeIns)) return;

    const placed = new Map<number, { sortOrder: number; onKerchief: boolean }>();
    slots.forEach((x, i) => x && placed.set(x.id, { sortOrder: i, onKerchief: true }));
    writeIns.forEach((x, k) => placed.set(x.id, { sortOrder: KERCHIEF_SLOT_COUNT + k, onKerchief: false }));
    const next = activities
      .map((x) => ({ ...x, ...placed.get(x.id) }))
      .sort((x, y) => (x.sortOrder ?? 0) - (y.sortOrder ?? 0) || x.name.localeCompare(y.name));
    setActivities(next);

    setReorderingActivity(true);
    try {
      await activitiesApi.reorder(slots.map((x) => x?.id ?? null), writeIns.map((x) => x.id));
    } catch {
      fetchActivities();
      toast.error("Failed to reorder activities");
    } finally {
      setReorderingActivity(false);
    }
  };

  const graduationAc = credits.find((c) => c.type === "graduation") ?? null;
  const visibleCredits = (showArchivedCredits
    ? credits
    : credits.filter((c) => !c.archivedAt)
  ).filter((c) => c.type !== "graduation");

  const columns = [
    {
      key: "order",
      header: "",
      render: (row: Activity) => {
        if (row.archivedAt) return null;
        const idx = activeActivityIndex.get(row.id) ?? -1;
        const prev = activeActivities[idx - 1];
        const nextRow = activeActivities[idx + 1];
        const canUp = !!prev && sameKerchiefGroup(prev, row);
        const canDown = !!nextRow && sameKerchiefGroup(nextRow, row);
        return (
          <div className="flex flex-col gap-0.5" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => handleReorderActivity(idx, "up")}
              disabled={!canUp || reorderingActivity}
              className="p-0.5 rounded text-stone-300 hover:text-stone-600 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
              title={prev && !canUp ? "Use Edit kerchief layout to move between printed and write-in activities" : "Move up"}
            >
              <ChevronUpIcon className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => handleReorderActivity(idx, "down")}
              disabled={!canDown || reorderingActivity}
              className="p-0.5 rounded text-stone-300 hover:text-stone-600 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
              title={nextRow && !canDown ? "Use Edit kerchief layout to move between printed and write-in activities" : "Move down"}
            >
              <ChevronDownIcon className="w-3.5 h-3.5" />
            </button>
          </div>
        );
      },
    },
    {
      key: "name",
      header: "Name",
      render: (row: Activity) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-stone-900">{row.name}</span>
          {row.archivedAt && <Badge label="Archived" color="stone" />}
        </div>
      ),
    },
    { key: "location", header: "Location" },
    {
      key: "credits",
      header: "Credits",
      render: (row: Activity) => (
        <span className="text-stone-600 tabular-nums">{(row.achievementCredits ?? []).filter((ac) => ac.type !== "graduation").length}</span>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (row: Activity) => (
        <button
          onClick={(e) => { e.stopPropagation(); openEdit(row); }}
          className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-forest-600 transition-colors"
          title="Edit activity"
        >
          <PencilSquareIcon className="w-4 h-4" />
        </button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Manage Activities"
        actions={
          <Button
            variant="primary"
            icon={<PlusIcon className="w-4 h-4" />}
            onClick={openCreate}
          >
            New Activity
          </Button>
        }
      />

      <div className="p-6 lg:p-8">
        {/* Show archived toggle */}
        <div className="flex items-center justify-between gap-2 mb-4">
          <label className="flex items-center gap-2 text-sm text-stone-500 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="rounded border-stone-300 text-forest-600 focus:ring-forest-500"
            />
            Show archived
          </label>
          <Link
            to="/admin/kerchief-layout"
            className="text-xs text-stone-400 hover:text-forest-700 underline underline-offset-2"
          >
            Edit kerchief layout
          </Link>
        </div>

        <Table
          columns={columns}
          data={showArchived ? activities : activeActivities}
          keyExtractor={(row) => row.id}
          loading={loading}
          emptyMessage="No activities yet."
        />
      </div>

      <Modal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingActivity(null); }}
        title={editingActivity ? "Edit Activity" : "Create Activity"}
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input label="Activity Name" error={errors.name?.message} {...register("name")} />
          <Input label="Location (optional)" {...register("location")} />
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1.5">Description</label>
            <textarea
              {...register("description")}
              rows={3}
              className="block w-full rounded-lg border border-stone-300 text-sm px-3 py-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent"
            />
          </div>

          {/* Badge */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1.5">Activity Badge</label>
            <div className="flex items-center gap-3">
              {badgePreview ? (
                <>
                  <img
                    src={badgePreview}
                    alt="Badge preview"
                    className="w-16 h-16 rounded-full object-cover border-2 border-dashed border-stone-300 flex-shrink-0"
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => badgeInputRef.current?.click()}
                      className="text-xs text-forest-600 hover:text-forest-700 font-medium underline"
                    >
                      Change
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setBadgePreview(null);
                        setBadgeFile(null);
                        setBadgeRemoved(true);
                        if (badgeInputRef.current) badgeInputRef.current.value = "";
                      }}
                      className="flex items-center gap-1 text-xs text-red-500 hover:text-red-600 font-medium"
                    >
                      <XMarkIcon className="w-3 h-3" />
                      Remove
                    </button>
                  </div>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => badgeInputRef.current?.click()}
                  className="flex items-center gap-2 text-sm text-stone-500 hover:text-stone-700 border border-dashed border-stone-300 rounded-lg px-3 py-2 transition-colors hover:border-stone-400"
                >
                  <PhotoIcon className="w-4 h-4" />
                  Upload Badge
                </button>
              )}
              <input
                ref={badgeInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setBadgeFile(file);
                  setBadgePreview(URL.createObjectURL(file));
                  setBadgeRemoved(false);
                }}
              />
            </div>
          </div>

          <div className="pt-2 space-y-3">
            {editingActivity && (
              <Button
                type="button"
                variant="outline"
                icon={<ListBulletIcon className="w-4 h-4" />}
                onClick={() => openManageCredits(editingActivity)}
                className="w-full"
              >
                Manage ACs
              </Button>
            )}
            <div className="flex items-center justify-between gap-3">
              {editingActivity ? (
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    icon={<ArchiveBoxIcon className="w-4 h-4" />}
                    onClick={handleArchive}
                    loading={archiving}
                  >
                    {editingActivity.archivedAt ? "Restore" : "Archive"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    icon={<TrashIcon className="w-4 h-4" />}
                    onClick={() => {
                      setActivityToDelete({ id: editingActivity.id, name: editingActivity.name });
                      setModalOpen(false);
                      setEditingActivity(null);
                      setDeleteConfirm(true);
                    }}
                  >
                    Delete
                  </Button>
                </div>
              ) : (
                <div />
              )}
              <div className="flex items-center gap-3">
                <Button type="button" variant="ghost" onClick={() => { setModalOpen(false); setEditingActivity(null); }}>Cancel</Button>
                <Button type="submit" variant="primary" loading={saving}>
                  {editingActivity ? "Save Changes" : "Create"}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={deleteConfirm}
        onClose={() => { setDeleteConfirm(false); setActivityToDelete(null); }}
        onConfirm={handleDelete}
        title="Delete Activity"
        message={`Are you sure you want to permanently delete "${activityToDelete?.name}"? This action cannot be undone.`}
        confirmLabel="Delete Forever"
        variant="danger"
        loading={deleting}
      />

      {/* Credits Management Modal */}
      <Modal
        open={creditsModalOpen}
        onClose={() => { setCreditsModalOpen(false); setManagingActivity(null); setCreditFormOpen(false); }}
        title={`Manage ACs — ${managingActivity?.name ?? ""}`}
        size="lg"
      >
        <div className="space-y-4">
          {/* Add / Edit form */}
          {creditFormOpen ? (
            <form ref={creditFormRef} onSubmit={handleSubmitCredit(onSubmitCredit)} className="bg-stone-50 rounded-lg p-4 space-y-3 border border-stone-200">
              <p className="text-sm font-medium text-stone-700">
                {editingCredit ? "Edit Credit" : "New Credit"}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Code"
                  placeholder="e.g. ARCH-1"
                  error={creditErrors.code?.message}
                  {...registerCredit("code")}
                />
                <Input
                  label="Name"
                  placeholder="e.g. Archery Level 1"
                  error={creditErrors.name?.message}
                  {...registerCredit("name")}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">Description (optional)</label>
                <textarea
                  {...registerCredit("description")}
                  rows={2}
                  className="block w-full rounded-lg border border-stone-300 text-sm px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent"
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button type="button" variant="ghost" size="sm" onClick={() => { setCreditFormOpen(false); setEditingCredit(null); }}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" size="sm" loading={savingCredit}>
                  {editingCredit ? "Save Changes" : "Add Credit"}
                </Button>
              </div>
            </form>
          ) : (
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-stone-500 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showArchivedCredits}
                  onChange={(e) => setShowArchivedCredits(e.target.checked)}
                  className="rounded border-stone-300 text-forest-600 focus:ring-forest-500"
                />
                Show archived
              </label>
              <Button
                variant="primary"
                size="sm"
                icon={<PlusIcon className="w-4 h-4" />}
                onClick={openCreditCreate}
              >
                Add Credit
              </Button>
            </div>
          )}

          {/* Graduation Achievement section */}
          {graduationAc && (
            <div className="rounded-lg border border-sky-200 bg-sky-50 p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AcademicCapIcon className="w-4 h-4 text-sky-600 shrink-0" />
                  <span className="text-sm font-semibold text-sky-700">Graduation Achievement</span>
                </div>
                <Badge label="Protected" color="stone" />
              </div>
              <p className="text-xs text-sky-600 mt-1">
                This achievement is automatically managed. Record it explicitly for each camper who graduates this activity.
              </p>
            </div>
          )}

          {/* Credits list */}
          <Table
            columns={[
              {
                key: "order",
                header: "",
                render: (row: AchievementCredit) => {
                  const idx = activeCreditIndex.get(row.id) ?? -1;
                  const isFirst = idx === 0;
                  const isLast = idx === activeCredits.length - 1;
                  if (row.archivedAt) return null;
                  return (
                    <div className="flex flex-col gap-0.5">
                      <button
                        onClick={() => handleReorderCredit(idx, "up")}
                        disabled={isFirst || reordering}
                        className="p-0.5 rounded text-stone-300 hover:text-stone-600 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                        title="Move up"
                      >
                        <ChevronUpIcon className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleReorderCredit(idx, "down")}
                        disabled={isLast || reordering}
                        className="p-0.5 rounded text-stone-300 hover:text-stone-600 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                        title="Move down"
                      >
                        <ChevronDownIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                },
              },
              {
                key: "displayNumber",
                header: "#",
                render: (row: AchievementCredit) => (
                  <span className="font-mono text-sm font-medium text-stone-800">
                    {row.archivedAt
                      ? <span className="text-stone-400">#{row.displayNumber}</span>
                      : row.displayNumber}
                  </span>
                ),
              },
              {
                key: "name",
                header: "Name",
                render: (row: AchievementCredit) => (
                  <div className="flex items-center gap-2">
                    <span className="text-stone-900">{row.name}</span>
                    {row.archivedAt && <Badge label="Archived" color="stone" />}
                  </div>
                ),
              },
              {
                key: "description",
                header: "Description",
                render: (row: AchievementCredit) => (
                  <span className="text-stone-500 text-sm truncate max-w-xs block">{row.description ?? "—"}</span>
                ),
              },
              {
                key: "actions",
                header: "",
                render: (row: AchievementCredit) => (
                  <div className="flex items-center gap-1 justify-end">
                    <button
                      onClick={() => openCreditEdit(row)}
                      className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-forest-600 transition-colors"
                      title="Edit credit"
                    >
                      <PencilSquareIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleArchiveCredit(row)}
                      disabled={archivingCredit}
                      className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-amber-600 transition-colors"
                      title={row.archivedAt ? "Restore credit" : "Archive credit"}
                    >
                      <ArchiveBoxIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => { setCreditToDelete(row); setDeleteCreditConfirm(true); }}
                      className="p-1.5 rounded-lg hover:bg-red-50 text-stone-400 hover:text-red-600 transition-colors"
                      title="Delete credit"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                ),
              },
            ]}
            data={visibleCredits}
            keyExtractor={(row) => row.id}
            loading={creditsLoading}
            emptyMessage="No achievement credits yet."
          />

          <div className="flex justify-end pt-1">
            <Button
              variant="ghost"
              onClick={() => { setCreditsModalOpen(false); setManagingActivity(null); setCreditFormOpen(false); }}
            >
              Close
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={deleteCreditConfirm}
        onClose={() => { setDeleteCreditConfirm(false); setCreditToDelete(null); }}
        onConfirm={handleDeleteCredit}
        title="Delete Achievement Credits"
        message={`Are you sure you want to permanently delete "${creditToDelete?.name}"? All completion records for this credit will also be deleted. This action cannot be undone.`}
        confirmLabel="Delete Forever"
        variant="danger"
        loading={deletingCredit}
      />
    </div>
  );
}
